import type pptxgen from "pptxgenjs";

import type {SingleASTNode} from "@khanacademy/simple-markdown";
import {markdownInlineParser} from "@khan/component-rich-text-editor";

import {defaultUnderlineOptions} from "./constants.ts";

/**
 * Processes a single section of markdown text into the format required for pptxgenjs.
 * Newline characters in the markdown are retained to allow for line breaks in the text.
 *
 * @param markdown text formatted in markdown.
 * @param allTextOptions options to apply to all the text.  This may be overwritten for portions of the text to correctly represent the markdown formatting.
 * @returns an array of text properties that can be used to create a text box in a pptx slide.
 */
export function markdownToTextProps(
    markdown: string,
    allTextOptions: pptxgen.TextPropsOptions = {},
): pptxgen.TextProps[] {
    const markdownNodes = markdownInlineParser(markdown);

    const nodes = markdownNodeArrayToTextProps(markdownNodes, allTextOptions);
    return nodes;
}

/**
 * Recursive entrypoint for converting a markdown AST to pptxgenjs text properties.
 *
 * @param nodes the nested markdown AST to convert.
 * @param allTextOptions the options to apply to all the text in the nested AST.
 * @returns a flat array of text properties that represents the nested markdown AST.
 */
function markdownNodeArrayToTextProps(
    nodes: SingleASTNode[],
    allTextOptions: pptxgen.TextPropsOptions,
): pptxgen.TextProps[] {
    const textSections: pptxgen.TextProps[] = [];

    for (const node of nodes) {
        const textProps = markdownNodeToTextProps(node, allTextOptions);
        if (textProps) {
            textSections.push(...textProps);
        }
    }

    return textSections;
}

/**
 * Recursive function that converts a single AST node to pptxgenjs text properties.
 *
 * @param node an AST node that represents a single text element.
 * @param allTextOptions the options to apply to all the text in the nested AST.  Certain properties may be overwritten based on node.type.
 * @returns a flat array of text properties that represents the content tree of the AST node.
 */
function markdownNodeToTextProps(
    node: SingleASTNode,
    allTextOptions: pptxgen.TextPropsOptions,
): pptxgen.TextProps[] {
    const textProps: pptxgen.TextProps[] = [];
    switch (node.type) {
        case "strong":
            return markdownNodeArrayToTextProps(node.content, {
                ...allTextOptions,
                bold: true,
            });

        case "em":
            return markdownNodeArrayToTextProps(node.content, {
                ...allTextOptions,
                italic: true,
            });

        case "u":
            return markdownNodeArrayToTextProps(node.content, {
                ...allTextOptions,
                underline: defaultUnderlineOptions,
            });
        case "inlineCode":
            textProps.push({
                text: node.content,
                options: {
                    ...allTextOptions,
                    fontFace: "Courier New",
                },
            });
            return textProps;
        case "inlineMath":
            return mathToTextProps(node.content, allTextOptions);
        case "link":
            return markdownNodeArrayToTextProps(node.content, {
                ...allTextOptions,
                hyperlink: {
                    url: node.target,
                },
            });
        case "text":
            // base case
            const text = node.content;
            if (typeof text === "string" && text.includes("\n")) {
                const lines = text.split("\n");
                const lastIndexToProcess = lines.length - 2; // set to second to last to avoid adding a line break to the part that comes after the last instance of \n
                for (let i = 0; i <= lastIndexToProcess; i++) {
                    textProps.push({
                        text: lines[i],
                        options: {
                            ...allTextOptions,
                            breakLine: true,
                        },
                    });
                }
                textProps.push({
                    text: lines[lines.length - 1],
                    options: allTextOptions,
                }); // add the part that comes after the last instance of \n
            } else {
                textProps.push({
                    text: node.content,
                    options: allTextOptions,
                });
            }
            return textProps;

        default:
            // base case
            textProps.push({
                text: node.content,
                options: allTextOptions,
            });
            return textProps;
    }
}

const logFunc = "\\log"; // log function in LaTeX
const naturalLogFunc = "\\ln"; // natural log function in LaTeX
const timesFunc = "\\times"; // times function in LaTeX
const divideFunc = "\\div"; // divide function in LaTeX
const cdotFunc = "\\cdot"; // cdot function in LaTeX
const integralFunc = "\\int"; // integral function in LaTeX
const piFunc = "\\pi"; // pi function in LaTeX
const textFunc = "\\text"; // text function in LaTeX
const spacerFunc = "\\,"; // spacer function in LaTeX
const fractionFunc = "\\frac"; // fraction function in LaTeX
const displayFractionFunc = "\\dfrac"; // display fraction function in LaTeX
const textFractionFunc = "\\tfrac"; // text fraction function in LaTeX

const fractionFunctions = new Set([
    fractionFunc,
    displayFractionFunc,
    textFractionFunc,
]);

const latexToSpecialChar: Map<string, string> = new Map([
    [timesFunc, "×"],
    [divideFunc, "÷"],
    [cdotFunc, "⋅"],
    [integralFunc, "∫"],
    [naturalLogFunc, "ln"],
    [piFunc, "π"],
    [logFunc, "log"],
    [spacerFunc, ""],
]);

// The supported color functions in our KA Mathjax renderer
// Found in: https://github.com/Khan/mathjax-renderer/blob/main/src/ts/tex-input-config.ts#L137-L187
const KAColorFunctions = new Set([
    "\\blue",
    "\\orange",
    "\\pink",
    "\\red",
    "\\green",
    "\\gray",
    "\\purple",
    "\\blueA",
    "\\blueB",
    "\\blueC",
    "\\blueD",
    "\\blueE",
    "\\tealA",
    "\\tealB",
    "\\tealC",
    "\\tealD",
    "\\tealE",
    "\\greenA",
    "\\greenB",
    "\\greenC",
    "\\greenD",
    "\\greenE",
    "\\goldA",
    "\\goldB",
    "\\goldC",
    "\\goldD",
    "\\goldE",
    "\\redA",
    "\\redB",
    "\\redC",
    "\\redD",
    "\\redE",
    "\\maroonA",
    "\\maroonB",
    "\\maroonC",
    "\\maroonD",
    "\\maroonE",
    "\\purpleA",
    "\\purpleB",
    "\\purpleC",
    "\\purpleD",
    "\\purpleE",
    "\\grayA",
    "\\grayB",
    "\\grayC",
    "\\grayD",
    "\\grayE",
    "\\grayF",
    "\\grayG",
    "\\grayH",
    "\\grayI",
    "\\kaBlue",
    "\\kaGreen",
    "\\mintA",
    "\\mintB",
    "\\mintC",
]);

const mathJaxColorFunctions = new Set(["\\color", "\\textcolor"]);

const SPACE = " ";
const TAB_CHARACTER = "    ";
const colorTypoFunction = "extcolor"; // this is a common typo produced by the LLM

const SUPERSCRIPT = "^";
const SUBSCRIPT = "_";
const FUNCTION_DELIMITER = "\\";

const extractFunction = (text: string): string => {
    const match = text.match(/^\\[a-z\t]+/i);
    return match ? match[0] : "";
};

const extractWhitespace = (text: string): string => {
    const match = text.match(/^\s+/);
    return match ? match[0] : "";
};

const extractWord = (text: string): string => {
    const match = text.match(/^[a-z]+/i);
    return match ? match[0] : "";
};

function mathToTextProps(
    math: string,
    allTextOptions: pptxgen.TextPropsOptions,
): pptxgen.TextProps[] {
    const parts: pptxgen.TextProps[] = [];
    let nextToAddIndex = 0;
    for (let i = 0; i < math.length; i++) {
        // process exponents as superscripts
        switch (math[i]) {
            case SUPERSCRIPT: {
                const {superscript, indexAdjustment} = extractSuperscript(
                    i,
                    math,
                );
                parts.push(
                    {
                        text: math.slice(nextToAddIndex, i),
                        options: allTextOptions,
                    },
                    {
                        text: superscript,
                        options: {
                            ...allTextOptions,
                            superscript: true,
                        },
                    },
                );
                i += indexAdjustment - 1;
                nextToAddIndex = i + 1;
                break;
            }
            case SUBSCRIPT: {
                const {subscript, indexAdjustment} = extractSubscript(i, math);
                parts.push(
                    {
                        text: math.slice(nextToAddIndex, i),
                        options: allTextOptions,
                    },
                    {
                        text: subscript,
                        options: {
                            ...allTextOptions,
                            subscript: true,
                        },
                    },
                );
                i += indexAdjustment - 1;
                nextToAddIndex = i + 1;
                break;
            }
            case FUNCTION_DELIMITER: {
                const latexFunction = extractFunction(math.slice(i));
                // process text function
                if (latexFunction === textFunc) {
                    const {text, indexAdjustment} = extractText(i, math);
                    parts.push(
                        {
                            text: math.slice(nextToAddIndex, i),
                            options: allTextOptions,
                        },
                        {
                            text: text,
                            options: allTextOptions,
                        },
                    );
                    i += indexAdjustment - 1;
                    nextToAddIndex = i + 1;
                } else if (fractionFunctions.has(latexFunction)) {
                    const {numerator, denominator, indexAdjustment} =
                        extractFractionComponents(i, latexFunction, math);
                    parts.push(
                        {
                            text: math.slice(nextToAddIndex, i),
                            options: allTextOptions,
                        },
                        ...nestedMathToTextProps(numerator, allTextOptions),
                        {
                            text: " / ",
                            options: allTextOptions,
                        },
                        ...nestedMathToTextProps(denominator, allTextOptions),
                    );
                    i += indexAdjustment - 1;
                    nextToAddIndex = i + 1;
                } else if (latexToSpecialChar.has(latexFunction)) {
                    const specialChar = latexToSpecialChar.get(latexFunction);
                    parts.push(
                        {
                            text: math.slice(nextToAddIndex, i),
                            options: allTextOptions,
                        },
                        {
                            text: specialChar,
                            options: allTextOptions,
                        },
                    );
                    nextToAddIndex = i + latexFunction.length;
                    i += latexFunction.length - 1;
                } else if (KAColorFunctions.has(latexFunction)) {
                    const {text, indexAdjustment} =
                        extractTextFromSingleBracketFunction(
                            i,
                            latexFunction,
                            math,
                        );
                    parts.push(
                        {
                            text: math.slice(nextToAddIndex, i),
                            options: allTextOptions,
                        },
                        ...mathToTextProps(
                            text,
                            allTextOptions, // don't adjust color here because we don't have colors that are appropriately contrasting on the slide background.
                        ),
                    );
                    i += indexAdjustment - 1;
                    nextToAddIndex = i + 1;
                } else if (mathJaxColorFunctions.has(latexFunction)) {
                    const {
                        firstPart: _,
                        secondPart: text,
                        indexAdjustment,
                    } = extractTextFromDoubleBracketFunction(
                        i,
                        latexFunction,
                        math,
                    );
                    parts.push(
                        {
                            text: math.slice(nextToAddIndex, i),
                            options: allTextOptions,
                        },
                        ...mathToTextProps(
                            text,
                            allTextOptions, // don't adjust color here because we don't have colors that are appropriately contrasting on the slide background.
                        ),
                    );
                    i += indexAdjustment - 1;
                    nextToAddIndex = i + 1;
                } else {
                    // did not recognize the function, so just remove the backslash
                    // and keep the function name in the final string
                    parts.push({
                        text: math.slice(nextToAddIndex, i),
                        options: allTextOptions,
                    });
                    nextToAddIndex = i + 1; // add 1 to skip the backslash
                    // do not add to i because it increments at the end of the loop
                }
                break;
            }
            case SPACE: {
                const spaces = extractWhitespace(math.slice(i));
                if (spaces === TAB_CHARACTER) {
                    const misformattedLatexFunction = extractWord(
                        math.slice(i + spaces.length),
                    );

                    if (misformattedLatexFunction === colorTypoFunction) {
                        const {
                            firstPart: _,
                            secondPart: text,
                            indexAdjustment:
                                misformattedFunctionIndexAdjustment,
                        } = extractTextFromDoubleBracketFunction(
                            i,
                            spaces + misformattedLatexFunction,
                            math,
                        );
                        parts.push(
                            {
                                text: math.slice(nextToAddIndex, i),
                                options: allTextOptions,
                            },
                            ...mathToTextProps(
                                text,
                                allTextOptions, // don't adjust color here because we don't have colors that are appropriately contrasting on the slide background.
                            ),
                        );
                        i +=
                            spaces.length +
                            misformattedFunctionIndexAdjustment -
                            1;
                        nextToAddIndex = i + 1;
                    }
                }
                break;
            }
            default:
                continue;
        }
    }
    if (nextToAddIndex < math.length) {
        parts.push({
            text: math.slice(nextToAddIndex),
            options: allTextOptions,
        });
    }
    return parts;
}

// Extracts arguments from a formatting function that has two sets of brackets.
// Format: "<functionName>{<firstPart>}{<secondPart>}"
// Example: "\\color{red}{text}"
function extractTextFromDoubleBracketFunction(
    i: number,
    keyword: string,
    math: string,
): {firstPart: string; secondPart: string; indexAdjustment: number} {
    const {text: firstPart, indexAdjustment: firstPartIndexAdjustment} =
        extractTextFromSingleBracketFunction(i, keyword, math);

    const indAfterFirstPart = i + firstPartIndexAdjustment;
    const {within: secondPart, bracketsCount: textBracketAdjustment} =
        extractFromBrackets(math.slice(indAfterFirstPart));

    return {
        firstPart,
        secondPart,
        indexAdjustment:
            firstPartIndexAdjustment +
            secondPart.length +
            textBracketAdjustment,
    };
}

// Extracts text from a formatting function that has two sets of brackets.
// Format: "<functionName>{<text>}"
// Example: "\\blueD{text}"
function extractTextFromSingleBracketFunction(
    i: number,
    keyword: string,
    math: string,
): {text: string; indexAdjustment: number} {
    const indAfterKeyword = i + keyword.length;

    const {within: text, bracketsCount: bracketAdjustment} =
        extractFromBrackets(math.slice(indAfterKeyword));

    return {
        text,
        indexAdjustment: keyword.length + text.length + bracketAdjustment,
    };
}

function extractFractionComponents(
    i: number,
    fractionKeyword: string,
    math: string,
): {numerator: string; denominator: string; indexAdjustment: number} {
    const {firstPart, secondPart, indexAdjustment} =
        extractTextFromDoubleBracketFunction(i, fractionKeyword, math);

    return {
        numerator: firstPart,
        denominator: secondPart,
        indexAdjustment,
    };
}

function extractText(
    i: number,
    math: string,
): {text: string; indexAdjustment: number} {
    return extractTextFromSingleBracketFunction(i, textFunc, math);
}

function extractSubscript(
    i: number,
    math: string,
): {subscript: string; indexAdjustment: number} {
    const {text: subscript, indexAdjustment} =
        extractFormattedTextInOptionalBrackets(i, SUBSCRIPT, math);
    return {subscript, indexAdjustment};
}

function extractSuperscript(
    i: number,
    math: string,
): {superscript: string; indexAdjustment: number} {
    const {text: superscript, indexAdjustment} =
        extractFormattedTextInOptionalBrackets(i, SUPERSCRIPT, math);
    return {superscript, indexAdjustment};
}

// Extracts text that is formatted by a function that optionally has brackets.
// If brackets are not included, the `text` is assumed to be a single character.
// Example: "^{foo}" returns 'foo' as `text` and "^f" returns 'f' as `text`.
function extractFormattedTextInOptionalBrackets(
    i: number,
    keyword: string,
    math: string,
): {text: string; indexAdjustment: number} {
    let text: string = "";
    let bracketAdjustment = 0;

    const indAfterKeyword = i + keyword.length;
    if (indAfterKeyword < math.length) {
        if (math[indAfterKeyword] === "{") {
            const {within, bracketsCount} = extractFromBrackets(
                math.slice(indAfterKeyword),
            );
            text = within;
            bracketAdjustment = bracketsCount;
        } else {
            text = math[indAfterKeyword];
        }
    }
    return {
        text,
        indexAdjustment: keyword.length + text.length + bracketAdjustment,
    };
}

function hasSingleTerm(text: string): boolean {
    const trimmedText = text.trim();
    const match = trimmedText.match(/[a-zA-Z0-9]+/); // match consecutive letters or digits to count the number of words and numbers
    return !!match && match[0].length === trimmedText.length;
}

function nestedMathToTextProps(
    math: string,
    allTextOptions: pptxgen.TextPropsOptions,
): pptxgen.TextProps[] {
    const parts: pptxgen.TextProps[] = [];

    if (hasSingleTerm(math)) {
        parts.push({
            text: math,
            options: allTextOptions,
        });
        return parts;
    }

    parts.push({
        text: "(",
        options: allTextOptions,
    });

    const nestedParts = mathToTextProps(math, allTextOptions);
    parts.push(...nestedParts);

    parts.push({
        text: ")",
        options: allTextOptions,
    });
    return parts;
}

/**
 * Extracts text from inside a pair of brackets.
 *
 * @param text a string that begins with '{' and may contain '}'.
 * @returns the string that is between the first '{' and the paired '}' (or end of the string if no '}' exists).
 */
const extractFromBrackets = (
    text: string,
): {within: string; bracketsCount: number} => {
    if (text.length === 0) {
        return {within: "", bracketsCount: 0};
    }
    if (text[0] !== "{") {
        return {within: text, bracketsCount: 0};
    }

    let end = -1;
    let openBrackets = 0;
    for (let i = 1; i < text.length; i++) {
        // use stack method to find the matching bracket
        if (text[i] === "{") {
            openBrackets++;
        } else if (text[i] === "}") {
            if (openBrackets === 0) {
                end = i;
                break;
            }
            openBrackets--;
        }
    }
    if (end === -1) {
        return {within: text.slice(1), bracketsCount: 1};
    }
    return {within: text.slice(1, end), bracketsCount: 2};
};
